import { useMemo } from "react";
import type { EquityPoint, AllocationData } from "../stores/backtestStore";
import type { DateRange } from "./charts/EquityChart";

interface RangeAnalysisPanelProps {
  equity: EquityPoint[];
  allocations: AllocationData[];
  tickerReturns: Record<string, number[]>;
  selectedRange: DateRange;
  onClear: () => void;
}

export function RangeAnalysisPanel({
  equity,
  allocations,
  tickerReturns,
  selectedRange,
  onClear,
}: RangeAnalysisPanelProps) {
  const analysis = useMemo(() => {
    if (!equity.length || !Object.keys(tickerReturns).length) return null;

    // Build date-to-index map for equity (tickerReturns[ticker][i] aligns with equity[i].date)
    const dateToIdx = new Map<string, number>();
    equity.forEach((pt, i) => dateToIdx.set(pt.date, i));

    // Build date-to-allocations map
    const dateToAlloc = new Map<string, Record<string, number>>();
    allocations.forEach((a) => dateToAlloc.set(a.date, a.allocations));

    // Find range boundaries
    const startIdx = dateToIdx.get(selectedRange.startDate);
    const endIdx = dateToIdx.get(selectedRange.endDate);
    if (startIdx === undefined || endIdx === undefined || startIdx >= endIdx) {
      return null;
    }

    // Get all tickers
    const tickers = Object.keys(tickerReturns);
    const contributions: Record<string, number> = {};
    tickers.forEach((t) => (contributions[t] = 0));

    // For each day in the range, accumulate contribution = prevDayWeight * dailyReturn
    // We iterate from startIdx+1 to endIdx (inclusive) — the return on day i is relative to day i-1
    let prevAllocations: Record<string, number> = {};
    // Look for allocation on or before startIdx
    for (let i = startIdx; i >= 0; i--) {
      const alloc = dateToAlloc.get(equity[i].date);
      if (alloc) {
        prevAllocations = alloc;
        break;
      }
    }

    for (let i = startIdx + 1; i <= endIdx; i++) {
      for (const ticker of tickers) {
        const weight = prevAllocations[ticker] ?? 0;
        const ret = tickerReturns[ticker]?.[i] ?? 0;
        contributions[ticker] += weight * ret;
      }
      // Update weights if we have allocations for this date
      const dayAlloc = dateToAlloc.get(equity[i].date);
      if (dayAlloc) {
        prevAllocations = dayAlloc;
      }
    }

    // Portfolio total return over the range
    const startValue = equity[startIdx].value;
    const endValue = equity[endIdx].value;
    const totalReturnPct = ((endValue / startValue) - 1) * 100;
    const totalReturnDollar = endValue - startValue;

    // Sort tickers by absolute contribution (descending)
    const sorted = tickers
      .map((ticker) => ({
        ticker,
        contribution: contributions[ticker] * 100, // convert to %
      }))
      .filter((t) => Math.abs(t.contribution) > 0.001)
      .sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));

    const maxAbs = sorted.length > 0
      ? Math.max(...sorted.map((t) => Math.abs(t.contribution)))
      : 1;

    return {
      sorted,
      maxAbs,
      totalReturnPct,
      totalReturnDollar,
      startValue,
      endValue,
      days: endIdx - startIdx,
    };
  }, [equity, allocations, tickerReturns, selectedRange]);

  if (!analysis) return null;

  const formatPct = (v: number) => {
    const sign = v >= 0 ? "+" : "";
    return `${sign}${v.toFixed(2)}%`;
  };

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(v);

  return (
    <div className="glass-card p-6 animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-surface-100">
            Range Analysis
          </h3>
          <span className="text-sm text-surface-400">
            {selectedRange.startDate} → {selectedRange.endDate} ({analysis.days} days)
          </span>
        </div>
        <button
          onClick={onClear}
          className="text-surface-400 hover:text-surface-200 text-sm px-2 py-1 rounded hover:bg-surface-700 transition-colors"
        >
          ✕ Clear
        </button>
      </div>

      {/* Summary row */}
      <div className="flex items-center gap-6 mb-5 pb-4 border-b border-surface-700/50">
        <div>
          <span className="text-surface-400 text-sm">Period Return</span>
          <div className={`text-lg font-bold ${analysis.totalReturnPct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
            {formatPct(analysis.totalReturnPct)}
          </div>
        </div>
        <div>
          <span className="text-surface-400 text-sm">P&L</span>
          <div className={`text-lg font-bold ${analysis.totalReturnDollar >= 0 ? "text-emerald-400" : "text-red-400"}`}>
            {formatCurrency(analysis.totalReturnDollar)}
          </div>
        </div>
        <div>
          <span className="text-surface-400 text-sm">Start</span>
          <div className="text-surface-200 text-sm">{formatCurrency(analysis.startValue)}</div>
        </div>
        <div>
          <span className="text-surface-400 text-sm">End</span>
          <div className="text-surface-200 text-sm">{formatCurrency(analysis.endValue)}</div>
        </div>
      </div>

      {/* Per-ticker contribution bars */}
      {analysis.sorted.length > 0 ? (
        <div className="space-y-2">
          <div className="text-sm text-surface-400 mb-3">Ticker Contributions</div>
          {analysis.sorted.map(({ ticker, contribution }) => {
            const widthPct = (Math.abs(contribution) / analysis.maxAbs) * 100;
            const isPositive = contribution >= 0;
            return (
              <div key={ticker} className="flex items-center gap-3">
                <span className="text-surface-200 text-sm font-mono w-12 text-right shrink-0">
                  {ticker}
                </span>
                <div className="flex-1 h-5 relative bg-surface-800 rounded overflow-hidden">
                  <div
                    className={`absolute top-0 h-full rounded ${isPositive ? "bg-emerald-500/60" : "bg-red-500/60"}`}
                    style={{
                      width: `${Math.max(widthPct, 1)}%`,
                      left: 0,
                    }}
                  />
                </div>
                <span
                  className={`text-sm font-medium w-16 text-right shrink-0 ${isPositive ? "text-emerald-400" : "text-red-400"}`}
                >
                  {formatPct(contribution)}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-surface-400 text-sm text-center py-4">
          No significant ticker contributions in this range.
        </div>
      )}
    </div>
  );
}
